import React, { Component } from 'react'
import styled from 'styled-components'
import type { IntlShape } from 'react-intl'
import { injectIntl } from 'react-intl'
import {
    CatalogSelectableOption,
    CatalogStepper,
    MorePersonalized
} from 'app/ecommerce/common/components/catalog'
import { updateOrderProductIngredient } from 'shared/utils/ecommerce/utils'
import type { PurchasableItem } from 'shared/store/ecommerce/order'
import type { EventMetadata } from 'shared/store/metadata'
import { eventsDefinitions } from 'shared/store/metadata'
import { isDefined } from 'shared/utils'

const maxFreeIngredientsToDisplay = 3

type Props = {
    intl: IntlShape,
    order: ?PurchasableItem,
    listIngredients: Array<any>,
    dispatchOrder: (order: ?PurchasableItem) => void,
    featureCustoAvailable: boolean,
    featureExtraAvailable: boolean,
    sendMeta: EventMetadata => void
}

type State = {
    products: any,
    personalize: boolean
}

class _CatalogStepperIngredients extends Component<Props, State> {
    maxIngredientToDisplay = 0
    listIngredientsToggled = []

    constructor(props: Props) {
        super(props)
        const {
            order,
            listIngredients,
            featureCustoAvailable,
            featureExtraAvailable
        } = this.props
        const products = listIngredients || []

        const indexExtra = products.findIndex(
            product => product.price === undefined || product.price === 0
        )

        this.listIngredientsToggled = featureCustoAvailable
            ? featureExtraAvailable
                ? products
                : products.slice(indexExtra, products.length)
            : products.slice(0, indexExtra)

        this.maxIngredientToDisplay =
            indexExtra || featureCustoAvailable
                ? maxFreeIngredientsToDisplay
                : 0

        this.state = this.getStateValues()
    }

    getStateValues = () => {
        const {
            order,
            featureCustoAvailable,
            featureExtraAvailable,
            listIngredients = []
        } = this.props
        const indexExtra = listIngredients.findIndex(
            product => !isDefined(product.price) || product.price === 0
        )
        return {
            products:
                featureCustoAvailable && !featureExtraAvailable
                    ? listIngredients.slice(
                          indexExtra,
                          indexExtra + this.maxIngredientToDisplay
                      )
                    : listIngredients.slice(0, this.maxIngredientToDisplay),
            personalize:
                featureCustoAvailable || featureExtraAvailable
                    ? listIngredients.length > this.maxIngredientToDisplay
                    : false
        }
    }

    updateOrderIngredient = (
        idIngredient: string,
        quantity: number,
        quantityInit: number,
        name: string,
        currentPrice: number,
        initialIdIngredient?: string
    ) => {
        const { order, dispatchOrder, sendMeta } = this.props
        const {
            ECOMM_ADD_INGREDIENT,
            ECOMM_REMOVE_INGREDIENT,
            GA4_ECOMM_ADD_INGREDIENT,
            GA4_ECOMM_REMOVE_INGREDIENT
        } = eventsDefinitions
        let event = ECOMM_REMOVE_INGREDIENT,
            ga4Event = GA4_ECOMM_REMOVE_INGREDIENT
        if (quantity > quantityInit) {
            event = ECOMM_ADD_INGREDIENT
            ga4Event = {
                ...GA4_ECOMM_ADD_INGREDIENT,
                ingredient_price: currentPrice
            }
        }
        sendMeta({ ...event, event_label: name })
        sendMeta({
            ...ga4Event,
            ingredient_name: name,
            ingredient_quantity: 1,
            item_name: order && order.label,
            item_id: order && order.id
        })

        const updateOrder = updateOrderProductIngredient(
            order,
            idIngredient,
            quantity,
            quantityInit,
            name,
            initialIdIngredient,
            currentPrice
        )
        dispatchOrder(
            updateOrderProductIngredient(
                order,
                idIngredient,
                quantity,
                quantityInit,
                name,
                initialIdIngredient,
                currentPrice
            )
        )
    }

    addIngredientToPersonalize = (moreProducts: boolean) => {
        this.setState({
            personalize: !moreProducts,
            products: moreProducts
                ? this.listIngredientsToggled
                : this.listIngredientsToggled.slice(
                      0,
                      this.maxIngredientToDisplay
                  )
        })
    }

    render() {
        const { intl, order } = this.props
        const { products, personalize } = this.state

        return (
            <TilesContainer>
                {products.map(
                    (
                        {
                            id,
                            name,
                            image,
                            quantityMax,
                            quantityMin,
                            initialQty = 0,
                            quantityInit,
                            price,
                            ingredientDefaultId
                        },
                        index
                    ) => {
                        const initQuantity =
                            quantityInit !== undefined
                                ? quantityInit
                                : initialQty
                        return (
                            <CatalogStepper
                                key={index}
                                id={id}
                                name={name}
                                quantityInit={initQuantity}
                                quantityMax={quantityMax}
                                quantityMin={quantityMin}
                                price={price}
                                onQuantityChange={(quantity: number) =>
                                    this.updateOrderIngredient(
                                        id,
                                        quantity,
                                        initialQty,
                                        name,
                                        price,
                                        ingredientDefaultId
                                    )
                                }
                                imageUrl={image}
                            />
                        )
                    }
                )}
                {personalize && (
                    <MorePersonalized
                        name={intl.formatMessage({
                            id: 'clickandcollect.catalog.product.personalize'
                        })}
                        onPress={() => this.addIngredientToPersonalize(true)}
                        isMore={true}
                    />
                )}
                {!personalize && products.length > this.maxIngredientToDisplay && (
                    <MorePersonalized
                        name={intl.formatMessage({
                            id:
                                'clickandcollect.catalog.product.personalize.less'
                        })}
                        onPress={() => this.addIngredientToPersonalize(false)}
                        isMore={false}
                    />
                )}
            </TilesContainer>
        )
    }
}

const TilesContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    margin-bottom: 20px;
    width: 100%;
`

const CatalogStepperIngredients = injectIntl(_CatalogStepperIngredients)

export { CatalogStepperIngredients }
